import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/** A rule with its latest evaluation; nulls mean it has never been evaluated. */
interface DqRule {
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly ruleName: string;
  readonly dimension: string;
  readonly severity: string;
  readonly targetMart: string;
  readonly statusCode: string | null;
  readonly score: number | null;
  readonly lastEvaluatedAt: string | null;
}

interface DqResult {
  readonly statusCode: string;
  readonly passCount: string;
  readonly failCount: string;
}

interface Mart {
  readonly martId: string;
  readonly martCode: string;
  readonly martName: string;
}

interface DqMeta {
  readonly dimensions: string[];
  readonly severities: string[];
}

/** M26 — Data Quality Dashboard (§17, BR-05). Authors rules over a catalogued
 *  mart (POST rules) with dimension/severity from the governed vocabulary
 *  (GET meta); evaluation runs the rule's SQL on the server and records the
 *  computed PASS/FAIL (POST rules/:id/evaluate) — nothing is typed by hand.
 *  Shows each rule's latest status, score and evaluation time, and exports
 *  the board as CSV (POST /v1/dwh/export). */
export function M26DataQualityDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<DqRule[]>([]);
  const [total, setTotal] = useState(0);
  const [marts, setMarts] = useState<Mart[]>([]);
  const [meta, setMeta] = useState<DqMeta>({ dimensions: [], severities: [] });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleCode, setRuleCode] = useState('');
  const [dimension, setDimension] = useState('');
  const [severity, setSeverity] = useState('');
  const [targetMart, setTargetMart] = useState('');
  const [sql, setSql] = useState('');

  const load = useCallback((): void => {
    setLoading(true);
    apiRequest<{ data: DqRule[]; total: number }>('/v1/dwh/data-quality/rules', { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      apiRequest<{ data: DqMeta }>('/v1/dwh/data-quality/meta', { token }),
      apiRequest<{ data: Mart[] }>('/v1/dwh/marts', { token }),
    ])
      .then(([m, c]) => { setMeta(m.data); setMarts(c.data); })
      .catch((e) => setError((e as Error).message));
  }, [token]);

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await apiRequest('/v1/dwh/data-quality/rules', {
        method: 'POST',
        token,
        body: {
          ruleName,
          ruleCode: ruleCode || undefined,
          dimension,
          severity,
          targetMart,
          expressionJson: { sql },
        },
      });
      setNotice(t('m26.dataQuality.ruleCreated'));
      setRuleName(''); setRuleCode(''); setDimension(''); setSeverity(''); setTargetMart(''); setSql('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function evaluate(ruleId: string): void {
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest<{ data: DqResult }>(`/v1/dwh/data-quality/rules/${encodeURIComponent(ruleId)}/evaluate`, {
      method: 'POST',
      token,
      body: {},
    })
      .then((r) => {
        setNotice(t('m26.dataQuality.ruleEvaluated', {
          status: r.data.statusCode,
          pass: number(Number(r.data.passCount)),
          fail: number(Number(r.data.failCount)),
        }));
        load();
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  function exportCsv(): void {
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest<string>('/v1/dwh/export', {
      method: 'POST',
      token,
      body: { entityType: 'DQ_RULES', rows, format: 'CSV' },
    })
      .then((csv) => {
        try {
          const blob = new Blob([typeof csv === 'string' ? csv : JSON.stringify(csv)], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'DQ_RULES.csv';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch {
          /* download unsupported (e.g. non-browser env) — export still succeeded server-side */
        }
        setNotice(t('m26.dataQuality.exportReady'));
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="data-quality-dashboard">
      <h1>{t('m26.dataQuality.title')}</h1>
      <form aria-label="dq-rule-form" onSubmit={onCreate}>
        <label htmlFor="dq-name">{t('m26.dataQuality.nameLabel')}</label>
        <input id="dq-name" value={ruleName} onChange={(e) => setRuleName(e.target.value)} required />
        <label htmlFor="dq-code">{t('m26.dataQuality.codeLabel')}</label>
        <input id="dq-code" value={ruleCode} onChange={(e) => setRuleCode(e.target.value)} />
        <label htmlFor="dq-dimension">{t('m26.dataQuality.dimensionLabel')}</label>
        <select id="dq-dimension" value={dimension} onChange={(e) => setDimension(e.target.value)} required>
          <option value="">{t('m26.dataQuality.choose')}</option>
          {meta.dimensions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label htmlFor="dq-severity">{t('m26.dataQuality.severityLabel')}</label>
        <select id="dq-severity" value={severity} onChange={(e) => setSeverity(e.target.value)} required>
          <option value="">{t('m26.dataQuality.choose')}</option>
          {meta.severities.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label htmlFor="dq-mart">{t('m26.dataQuality.martLabel')}</label>
        <select id="dq-mart" value={targetMart} onChange={(e) => setTargetMart(e.target.value)} required>
          <option value="">{t('m26.dataQuality.choose')}</option>
          {marts.map((m) => <option key={m.martId} value={m.martCode}>{`${m.martCode} — ${m.martName}`}</option>)}
        </select>
        <label htmlFor="dq-sql">{t('m26.dataQuality.sqlLabel')}</label>
        <textarea id="dq-sql" value={sql} onChange={(e) => setSql(e.target.value)} rows={4} required />
        <p>{t('m26.dataQuality.sqlHelp')}</p>
        <button type="submit" disabled={busy}>{t('m26.dataQuality.createRule')}</button>
      </form>
      <button type="button" aria-label="export-dq" disabled={busy || rows.length === 0} onClick={exportCsv}>{t('m26.dataQuality.exportCsv')}</button>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.dataQuality.loading')}</p> : (
        <>
          <p aria-label="dq-count">{t('m26.dataQuality.count', { count: total })}</p>
          <table aria-label="dq-table">
            <thead><tr><th>{t('m26.dataQuality.colCode')}</th><th>{t('m26.dataQuality.colName')}</th><th>{t('m26.dataQuality.colDimension')}</th><th>{t('m26.dataQuality.colSeverity')}</th><th>{t('m26.dataQuality.colMart')}</th><th>{t('m26.dataQuality.colStatus')}</th><th>{t('m26.dataQuality.colScore')}</th><th>{t('m26.dataQuality.colEvaluated')}</th><th>{t('m26.dataQuality.colActions')}</th></tr></thead>
            <tbody>{rows.map((d) => (
              <tr key={d.ruleId}>
                <td>{d.ruleCode}</td><td>{d.ruleName}</td><td>{d.dimension}</td><td>{d.severity}</td><td>{d.targetMart}</td>
                <td>{d.statusCode ?? t('m26.dataQuality.notEvaluated')}</td>
                <td>{d.score == null ? '' : `${number(d.score, 1)}%`}</td>
                <td>{at(d.lastEvaluatedAt)}</td>
                <td><button type="button" aria-label={`evaluate-${d.ruleId}`} disabled={busy} onClick={() => evaluate(d.ruleId)}>{t('m26.dataQuality.evaluate')}</button></td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
